import InternalChatAPI from '../../api/internalChat';

const state = {
    conversations: [],
    activeConversationId: null,
    messages: {},
    availableUsers: [],
    uiFlags: {
        isFetchingConversations: false,
        isFetchingMessages: false,
        isSending: false,
        isPanelOpen: false,
    },
};

const getters = {
    getConversations: s => s.conversations.slice().sort((a, b) => {
        // System conversations always first
        if (a.system && !b.system) return -1;
        if (!a.system && b.system) return 1;
        const aTime = a.last_message_at || a.created_at;
        const bTime = b.last_message_at || b.created_at;
        return new Date(bTime) - new Date(aTime);
    }),
    getActiveConversationId: s => s.activeConversationId,
    getActiveMessages: s => s.messages[s.activeConversationId] || [],
    getAvailableUsers: s => s.availableUsers,
    getUIFlags: s => s.uiFlags,
    getTotalUnreadCount: s =>
        s.conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0),
    isPanelOpen: s => s.uiFlags.isPanelOpen,
};

const mutations = {
    SET_CONVERSATIONS(st, conversations) {
        st.conversations = conversations;
    },
    SET_ACTIVE_CONVERSATION(st, id) {
        st.activeConversationId = id;
    },
    SET_MESSAGES(st, { conversationId, messages }) {
        st.messages = { ...st.messages, [conversationId]: messages };
    },
    ADD_MESSAGE(st, { conversationId, message }) {
        const msgs = st.messages[conversationId] || [];
        // avoid duplicates
        if (!msgs.find(m => m.id === message.id)) {
            st.messages = { ...st.messages, [conversationId]: [...msgs, message] };
        }
    },
    UPDATE_CONVERSATION_LAST_MESSAGE(st, { conversationId, message }) {
        const conv = st.conversations.find(c => c.id === conversationId);
        if (conv) {
            conv.last_message = {
                content: message.content,
                created_at: message.created_at,
                sender_id: message.user_id,
            };
            conv.last_message_at = message.created_at;
        }
    },
    INCREMENT_UNREAD(st, conversationId) {
        const conv = st.conversations.find(c => c.id === conversationId);
        if (conv) conv.unread_count = (conv.unread_count || 0) + 1;
    },
    CLEAR_UNREAD(st, conversationId) {
        const conv = st.conversations.find(c => c.id === conversationId);
        if (conv) conv.unread_count = 0;
    },
    SET_AVAILABLE_USERS(st, users) {
        st.availableUsers = users;
    },
    SET_UI_FLAG(st, flag) {
        st.uiFlags = { ...st.uiFlags, ...flag };
    },
    ADD_CONVERSATION(st, conversation) {
        if (!st.conversations.find(c => c.id === conversation.id)) {
            st.conversations.push(conversation);
        }
    },
    TOGGLE_PANEL(st) {
        st.uiFlags = { ...st.uiFlags, isPanelOpen: !st.uiFlags.isPanelOpen };
    },
};

const actions = {
    async fetchConversations({ commit }) {
        commit('SET_UI_FLAG', { isFetchingConversations: true });
        try {
            const { data } = await InternalChatAPI.getConversations();
            commit('SET_CONVERSATIONS', data);
        } finally {
            commit('SET_UI_FLAG', { isFetchingConversations: false });
        }
    },

    async fetchMessages({ commit }, conversationId) {
        commit('SET_UI_FLAG', { isFetchingMessages: true });
        try {
            const { data } = await InternalChatAPI.getMessages(conversationId);
            commit('SET_MESSAGES', { conversationId, messages: data.messages });
        } finally {
            commit('SET_UI_FLAG', { isFetchingMessages: false });
        }
    },

    async createConversation({ commit }, userId) {
        const { data } = await InternalChatAPI.createConversation(userId);
        commit('ADD_CONVERSATION', data);
        return data;
    },

    async sendMessage({ commit }, { conversationId, content }) {
        commit('SET_UI_FLAG', { isSending: true });
        try {
            const { data } = await InternalChatAPI.sendMessage(conversationId, content);
            commit('ADD_MESSAGE', { conversationId, message: data });
            commit('UPDATE_CONVERSATION_LAST_MESSAGE', { conversationId, message: data });
        } finally {
            commit('SET_UI_FLAG', { isSending: false });
        }
    },

    async markRead({ commit }, conversationId) {
        commit('CLEAR_UNREAD', conversationId);
        await InternalChatAPI.markRead(conversationId);
    },

    async fetchAvailableUsers({ commit }) {
        const { data } = await InternalChatAPI.getAvailableUsers();
        commit('SET_AVAILABLE_USERS', data);
    },

    setActiveConversation({ commit, dispatch }, conversationId) {
        commit('SET_ACTIVE_CONVERSATION', conversationId);
        if (conversationId) {
            dispatch('fetchMessages', conversationId);
            dispatch('markRead', conversationId);
        }
    },

    // Called from ActionCable
    addMessage({ commit, state: st, rootGetters }, data) {
        const currentUserId = rootGetters.getCurrentUser?.id;
        const { internal_conversation_id: convId } = data;
        const message = {
            id: data.id,
            content: data.content,
            user_id: data.user_id,
            sender_name: data.sender_name,
            sender_avatar: data.sender_avatar,
            created_at: data.created_at,
        };
        commit('ADD_MESSAGE', { conversationId: convId, message });
        commit('UPDATE_CONVERSATION_LAST_MESSAGE', { conversationId: convId, message });

        // Increment unread if not the active conversation or the sender
        if (data.user_id !== currentUserId &&
            (st.activeConversationId !== convId || !st.uiFlags.isPanelOpen)) {
            commit('INCREMENT_UNREAD', convId);
        }
    },

    togglePanel({ commit, dispatch, state: st }) {
        commit('TOGGLE_PANEL');
        if (!st.uiFlags.isPanelOpen === false) {
            // panel just opened
            dispatch('fetchConversations');
        }
    },

    openPanel({ commit, dispatch }) {
        commit('SET_UI_FLAG', { isPanelOpen: true });
        dispatch('fetchConversations');
    },

    closePanel({ commit }) {
        commit('SET_UI_FLAG', { isPanelOpen: false });
        commit('SET_ACTIVE_CONVERSATION', null);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
